/**
 * Reactive "can the current user reach this path" check for navigation UI.
 *
 * Nav nodes don't carry their own `auth` flag — auth rules live in a separate
 * path-keyed map (`/auth-rules.json`, lazy-loaded via `getAuthRules`). This
 * joins the two so menu links / CTAs can be muted when the visitor can't (yet)
 * access their target. Purely cosmetic — the real gate is enforced by
 * `middleware/auth.global.ts` + `server/middleware/auth.ts` on navigation.
 *
 * The rules map is cached in shared state (fetched once per SPA session) and
 * `isMuted` reads both it and the reactive `useAuth` session, so links re-render
 * from faded → normal the moment auth hydrates or the user signs in.
 */
import { getAuthRules } from "~/composables/useNavConfig";
import type { AuthRule } from "#shared/authRule";

export function useCanAccess() {
  const rules = useState<Record<string, AuthRule> | null>(
    "auth:rules",
    () => null,
  );
  const { evaluateRule, user } = useAuth();

  if (import.meta.client && rules.value === null) {
    void getAuthRules().then((r) => {
      rules.value = r;
    });
  }

  const norm = (path: string): string => {
    const q = path.indexOf("?");
    const base = q >= 0 ? path.slice(0, q) : path;
    return base.length > 1 && base.endsWith("/") ? base.slice(0, -1) : base;
  };

  /** True when `path` is auth-gated and the current session can't satisfy it. */
  function isMuted(path: string): boolean {
    // Touch the session so callers using `isMuted` in a template track it.
    void user.value;
    const rule = rules.value?.[norm(path)];
    if (rule === undefined || rule === false) return false;
    return !evaluateRule(rule);
  }

  return { isMuted };
}
